<template>
  <div>
    <section-header v-if="!hideTitle"
                    :ressource="ressource"
                    :isExtended="true"
                    :dialog="dialog"
                    @closeDialog="closeDialog">
    </section-header>
    <v-row class="pt-10 px-5">
      <template v-for="(field, index) in editables">
        <v-col class="col-12 py-0" v-if="controls(field) != null" :key="index">
          <component ref="SECTION_DIALOG" :is="controls(field)" v-bind="currentProperties(field)" v-on="events(field)" ></component>
        </v-col>
        <v-col class="col-12 py-0" v-else-if="editorType(field.type) == fieldTypes.STRING" :key="index + fieldTypes.STRING">
          <edit-field :value="getEditValue(field.value)"
                      :editFieldInfo="fieldInfoMap.get(field.id)"
                      :min="field.min"
                      :max="field.max"
                      :disabled="field.readOnly"
                      @updated="(value) => editFieldUpdated(value, field.value, field.type)">
          </edit-field>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) == fieldTypes.DATE" :key="index + fieldTypes.DATE">
          <date-field :value="getEditValue(field.value)"
                      :editFieldInfo="fieldInfoMap.get(field.id)"
                      :disabled="field.readOnly"
                      @updated="(value) => editFieldUpdated(value, field.value, field.type)">
          </date-field>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.CURRENCY" :key="index + fieldTypes.CURRENCY">
          <edit-currency :libelle="field.text"
                         :value="getEditValue(field.value)"
                         @input="(value) => setCurrencyField(value, field.value)">
          </edit-currency>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.HTMLATTRIB && htmlAttribRefs.length > 0" :key="index + fieldTypes.HTMLATTRIB">
          <html-field v-if="getHtmlText(field) != null"
                      :label="getHtmlAttriblabel(field, false)"
                      :value="getHtmlText(field).html"
                      :params="getHtmlText(field).publiParams"
                      :disabled="field.readOnly"
                      @updated="(value) => setHtmlField(value, field)">
          </html-field>
          <html-field v-if="getHtmlText(field) != null && adminInfo && adminInfo.hasHtmlMobile"
                      :label="getHtmlAttriblabel(field, true)"
                      :value="getHtmlText(field).htmlMobile"
                      :params="getHtmlText(field).publiParams"
                      :disabled="field.readOnly"
                      @updated="(value) => setHtmlMobileField(value, field)">
          </html-field>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.BOOL" :key="index + fieldTypes.BOOL">
          <check-field :value="obj[field.value]"
                       :editFieldInfo="fieldInfoMap.get(field.id)"
                       :disabled="field.readOnly"
                       @updated="(value) => setBooleanField(value, field.value)">
          </check-field>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.CHOIX && field.foreignClass != ''" :key="index + fieldTypes.CHOIX + 1">
          <admin-summary  :value="getSummaryField(field)"
                          :editFieldInfo="fieldInfoMap.get(field.id)"
                          :ressource="ressourceSummary(field)"
                          :tableName="field.foreignTableName"
                          :tableClass="field.foreignClass"
                          :objName="getObjName(field)"
                          :publiContext="getPubliContext()"
                          @update:value="(value) => setSummaryField(value, field)">
          </admin-summary>
        </v-col>

        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.CHOIX" :key="index + fieldTypes.CHOIX">
          <select-field :editFieldInfo="fieldInfoMap.get(field.id)"
                        :value="getSelectValue(field)"
                        :items="getSelectItems(field)"
                        :disabled="field.readOnly"
                        @updated="(value) => setSelectField(value, field)">
          </select-field>
        </v-col>
        <v-col class="col-12 py-0" v-else-if="editorType(field.type) === fieldTypes.IMAGE" :key="index + fieldTypes.IMAGE">
          <v-file-input v-model="file"
                      accept="image/*"
                      label="asfsa"
                      @change="(value: any) => setImage(value, field.value)">
          </v-file-input>
          <v-img :src='getImage(field.value)'></v-img>
        </v-col>

      </template>
    </v-row>

    <div class="d-flex flex-row-reverse mt-4">
      <loader-button class="ms-2"
                     type="submit"
                     :label="applyButtonText"
                     :disabled="!updated"
                     @callback="apply">
      </loader-button>
    </div>
  </div>
</template>

<script lang="ts">
class HtmlAttribRef {
  htmlAttrib: HtmlAttrib;
  adminAttribId: number;
}

import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import { MapRessource } from '@/framework/base/helpers/Ressources';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import { EditFieldTheme, EditFieldType, FieldInfo } from '@/framework/base/components/FieldInfo';
import EditCurrency from '@/framework/base/components/EditCurrency.vue';
import EditField from '@/framework/base/components/EditField.vue';
import DateField from '@/framework/base/components/DateField.vue';
import CheckField from '@/framework/base/components/CheckField.vue';
import HtmlField from '@/framework/base/components/HtmlField.vue';
import Item from '@/framework/base/classes/Item';
import SelectField from '@/framework/base/components/SelectField.vue';
import AdminAttrib from '@/generated/classes/AdminAttrib';
import HtmlAttrib from '@/generated/classes/HtmlAttrib'
import { FwkButtons, RessourceFwkButton } from '@/ressources/globalRessources';
import {setObjName, save, getObjName, getSelectValue, getPubliContext, getHtmlAttrib} from '@/extend/sections/SectionAdminExtend';
import AdminSummary from './AdminSummary.vue';
import SectionAdminControl from '@/generated/sections/SectionAdminControl.vue';
import AdminInfo from '@/generated/classes/AdminInfo';
import { defineComponent, type PropType } from "vue";

export default defineComponent({
  components: {
    SelectField,
    HtmlField,
    CheckField,
    EditField,
    DateField,
    SectionHeader,
    LoaderButton,
    EditCurrency,
    AdminSummary
  },
  extends: SectionAdminControl,
  props: {
    hideTitle: {
      type: Boolean
    },
    data: {
      type: Object as PropType<any>,
      required: true
    },
    config: {
      type: Array as PropType<AdminAttrib[]>,
      required: true
    },
    dialog: {
      type: Boolean
    },
    closeSection: {
      type: Boolean
    },
    adminInfo: {
      type: Object as PropType<AdminInfo>,
      required: true
    }
  },
  created() {
    this.getEditables();
    this.editables.forEach((adminAttrib: AdminAttrib) => this.fieldInfoMap.set(adminAttrib.id, this.fieldInfo(adminAttrib)));
    setObjName(this.obj, this.adminInfo);
  },
  data() {
    const htmlCloneAttribs: HtmlAttribRef[] = [];
    const htmlAttribRefs: HtmlAttribRef[] = [];
    const fieldInfoMap: Map<number, FieldInfo> = new Map();
    const editables: AdminAttrib[] = [];
    const file: File | null = null;
    const clone: any = null;

    return {
      clone,
      file,
      editables,
      htmlAttrib: false,
      fieldInfoMap,
      htmlAttribRefs,
      htmlCloneAttribs,
      updated: false
    };
  },
  computed: {
    ressource() {
      const ressource = new MapRessource();
      ressource.label = this.adminInfo && this.adminInfo.label ? this.adminInfo.label : this.crudTitle;
      if (this.adminInfo) {
        ressource.helpHtmlId = this.adminInfo.helpHtmlId;
      }
      return ressource;
    },
    crudTitle(): string {
      if (this.data.id > 0) {
        return 'Modifier';
      } else {
        return 'Nouveau';
      }
    },
    obj(): any {
      if (this.clone == null || this.clone.id != this.data.id) {
        this.setClone(this.data);
      }
      return this.clone;
    },
    applyButtonText(): string {
      if (this.data.id > 0) {
        return RessourceFwkButton(FwkButtons.APPLIQUER).label;
      } else {
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      }
    },
    fieldTypes() {
      return EditFieldType;
    }
  },

  methods: {
    setClone(data: any) {
      this.clone = JSON.parse(JSON.stringify(data));
    },
    ressourceSummary(field: AdminAttrib): MapRessource {
      const ressource = new MapRessource();
      ressource.label = field.foreignTableName;
      return ressource;
    },
    events(field: AdminAttrib): Record<string, (payload: any) => void> {
      return {
        'update:value': (value: any) => this.editFieldUpdated(value, field.value, field.type),
      };
    },
    currentProperties(field: AdminAttrib) {
      const ressource = new MapRessource();
      ressource.label = field.text;
      return {
        theme: EditFieldTheme.BASIC,
        value: this.getEditValue(field.value),
        required: false,
        ressource: ressource
      };
    },
    getEditables() {
      this.editables = this.config
          .filter(att => att.displayed)
          .sort((a, b) => a.tableCol - b.tableCol);

      this.editables.forEach((item) => {
        if (item.type == 'htmlAttrib') {
          this.getHtmlAttrib(parseInt(this.obj[item.value]), 'Aide', this.obj.name, item);
        }
      })
    },
    editorType(type: string): EditFieldType {
      if (type == "bool") {
        return EditFieldType.BOOL;
      } else if (type == "string" || type == "int" || type == "decimal") {
        return EditFieldType.STRING;
      } else if (type == "DateTime") {
        return EditFieldType.DATE;
      } else if (type == "currency") {
        return EditFieldType.CURRENCY;
      } else if (type == "choix" || type.startsWith('enum')) {
        return EditFieldType.CHOIX;
      } else if (type == "html") {
        return EditFieldType.HTMLATTRIB;
      } else if (type == "htmlAttrib") {
        return EditFieldType.HTMLATTRIB;
      } else if (type == "image") {
        return EditFieldType.IMAGE;
      } else {
        return EditFieldType.NONE;
      }
    },
    fieldInfoType(type: string): EditFieldType {
      if (type == "bool") {
        return EditFieldType.BOOL;
      } else if (type == "string") {
        return EditFieldType.STRING;
      } else if (type == "int") {
        return EditFieldType.INT;
      } else if (type == "decimal") {
        return EditFieldType.DECIMAL;
      } else if (type == "DateTime") {
        return EditFieldType.DATE;
      } else if (type == "currency") {
        return EditFieldType.CURRENCY;
      } else if (type == "choix" || type.startsWith('enum')) {
        return EditFieldType.CHOIX;
      } else if (type == "html") {
        return EditFieldType.HTML;
      } else if (type == "htmlAttrib") {
        return EditFieldType.HTMLATTRIB;
      } else {
        return EditFieldType.NONE;
      }
    },
    fieldInfo(field: AdminAttrib): FieldInfo {
      const ressource = new MapRessource();
      ressource.label = field.text;
      const type = this.fieldInfoType(field.type);
      const precision = 2;
      const format = null;
      const mask = null;
      const live = null;
      const focus = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live);
    },
    getHtmlAttriblabel(field: AdminAttrib, mobile: boolean): string {
      if (mobile) {
        return field.text + ' (version mobile)';
      } else {
        return field.text;
      }
    },
    getEditValue(objField: string): string {
      let res = '';
      const objArray = objField.split(':');
      if (objArray.length == 1) {
        const value = this.obj[objArray[0]];
        if (value) res = value;
      } else {
        const index = parseInt(objArray[1]);
        const values = this.obj[objArray[0]];
        if (values && values.length > index) {
          const value = values[index];
          if (value) res = value;
        }
      }
      return res;
    },
    getImage(objField: string): string {
      let res = '';
      const objArray = objField.split(':');
      if (objArray.length == 1) {
        const value = this.obj[objArray[0]];
        if (value) res = value;
      }
      return res;
    },
    getHtmlAttrib(value: number, objType: string, objName: string, item: AdminAttrib) {
      if (value > 0) {
        HtmlAttrib.get(value).then((res) => {
          {
            const htmlAttribValue = new HtmlAttribRef();
            htmlAttribValue.htmlAttrib = res;
            htmlAttribValue.adminAttribId = item.id;
            this.htmlAttribRefs.push(htmlAttribValue);
          }
          {
            const htmlAttribValue = new HtmlAttribRef();
            htmlAttribValue.htmlAttrib = HtmlAttrib.fromJson(res);
            htmlAttribValue.adminAttribId = item.id;
            this.htmlCloneAttribs.push(htmlAttribValue);
          }
        });
      } else {
        const htmlAttribValue = new HtmlAttribRef();
        const newHtmlAttrib = getHtmlAttrib(item, this.adminInfo, objType, objName);
        htmlAttribValue.htmlAttrib = newHtmlAttrib;
        htmlAttribValue.adminAttribId = item.id;
        this.htmlAttribRefs.push(htmlAttribValue);
      }
    },
    getObjName(field: AdminAttrib): string {
      let res = "";
      res = getObjName(field, this.obj, this.adminInfo.objType);
      return res;
    },
    getPubliContext(): string {
      let res = "";
      res = getPubliContext(this.obj, this.adminInfo.objType);
      return res;
    },
    getHtmlText(field: AdminAttrib): HtmlAttrib | null {
      const htmlAttribValue = this.htmlAttribRefs.find(a => a.adminAttribId == field.id);
      if (htmlAttribValue) {
        return htmlAttribValue.htmlAttrib;
      } else {
        return null;
      }
    },
    editFieldUpdated(value: string, objField: string, fieldType: string) {
      const objFieldType = this.fieldInfoType(fieldType);
      const objArray = objField.split(':');
      if (objArray.length == 1) {
        if (objFieldType === EditFieldType.INT) {
          this.obj[objField] = parseInt(value);
        } else if (objFieldType === EditFieldType.DECIMAL) {
          this.obj[objField] = parseFloat(value);
        } else {
          this.obj[objField] = value;
        }
      } else {
        const index = parseInt(objArray[1]);
        if (objFieldType === EditFieldType.INT) {
          this.obj[objArray[0]][index] = parseInt(value);
        } else if (objFieldType === EditFieldType.DECIMAL) {
          this.obj[objArray[0]][index] = parseFloat(value);
        } else {
          this.obj[objArray[0]][index] = value;
        }
      }
      if ((objFieldType === EditFieldType.INT ||
        objFieldType === EditFieldType.DECIMAL) &&
        isNaN(this.obj[objField])) {
        this.obj[objField] = 0;
      }
      this.checkChanges();
    },
    getSelectItems(field: AdminAttrib): Item[] {
      const items: Item[] = [];
      field.choix.forEach(c => {
        items.push(new Item(c.value, c.text));
      });
      return items;
    },
    getSelectValue(field: AdminAttrib): string {
      let res = '';
      const value = this.obj[field.value];
      res = getSelectValue(this.getSelectItems(field), value, field);
      return res;
    },
    getSummaryField(field: AdminAttrib): number {
      return this.obj[field.value];
    },
    setSummaryField(value: number, objField: AdminAttrib) {
      this.obj[objField.value] = value;
      this.checkChanges();
    },
    setSelectField(selected: Item, objField: AdminAttrib) {
      this.obj[objField.value] = selected.key;
      if (objField.choix && objField.choix.length == 2 && objField.choix[0].text == 'Non' && objField.choix[1].text == 'Oui') {
        this.obj[objField.value] = selected.key == 0 ? false : true;
      }
      this.checkChanges();
    },
    setCurrencyField(value: string, objField: string) {
      this.obj[objField] = parseFloat(value);
      if (isNaN(this.obj[objField])) {
        this.obj[objField] = 0;
      } this.checkChanges();
    },
    setBooleanField(value: boolean, objField: string) {
      this.obj[objField] = value == true ? 'true' : 'false';
      this.checkChanges();
    },
    setHtmlField(value: string, field: AdminAttrib) {
      const htmlAttribValue = this.htmlAttribRefs.find(a => a.adminAttribId == field.id);
      if (htmlAttribValue) {
        htmlAttribValue.htmlAttrib.html = value;
      }
      this.checkChanges();
    },
    setHtmlMobileField(value: string, field: AdminAttrib) {
      const htmlAttribValue = this.htmlAttribRefs.find(a => a.adminAttribId == field.id);
      if (htmlAttribValue) {
        htmlAttribValue.htmlAttrib.htmlMobile = value;
      }
      this.checkChanges();
    },
    setImage(file: File, objField: string) {
      const reader = new FileReader();
      reader.onload = (event) => {
        // @ts-expect-error : generated method
        this.obj[objField] = event.target.result;
        this.checkChanges();
      }
      reader.readAsDataURL(file);
    },
    checkChanges(): void {
      this.updated = JSON.stringify(this.data) != JSON.stringify(this.clone) ||
          JSON.stringify(this.htmlAttribRefs) != JSON.stringify(this.htmlCloneAttribs);
    },
    async saveObj(obj: any) {
        //
    },
    async apply() {
      if (this.updated) {
        if (this.htmlAttribRefs.length > 0) {
          for (let i = 0; i <= this.htmlAttribRefs.length - 1; i++) {
            const res = this.htmlAttribRefs[i];
            if (res.htmlAttrib.id > 0) {
              await res.htmlAttrib.update();
            } else if (res.htmlAttrib.id <= 0) {
              const result = await res.htmlAttrib.create();
              save(this.obj, result, this.adminInfo.objType);
            }
          }
        }
        this.$emit('updated', this.obj);
        this.$emit('closeDialog');
      }
    },
    closeDialog() {
      this.$emit('closeDialog');
    }
  }
})

</script>

<style scoped>
</style>
